// src/components/productTypes/ProductTypesTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetProductTypes from "../../hooks/productTypes/useGetProductTypes";
import useDeleteProductTypes from "../../hooks/productTypes/useDeleteProductTypes";
import useDeleteAllProductTypes from "../../hooks/productTypes/useDeleteAllProductTypes";
import useRestoreProductType from "../../hooks/productTypes/useRestoreProductType";
import useRestoreAllProductTypes from "../../hooks/productTypes/useRestoreAllProductTypes";
import useForceDeleteProductType from "../../hooks/productTypes/useForceDeleteProductType";
import useForceDeleteAllProductTypes from "../../hooks/productTypes/useForceDeleteAllProductTypes";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import ProductTypesTableRow from "./Table/ProductTypesTableRow";
import ProductTypesTableColumns from "./Table/ProductTypesTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const ProductTypesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("productTypes_list"),
            create: has("productTypes_create"),
            edit: has("productTypes_edit"),
            delete: has("productTypes_delete"),
            forceDelete: has("productTypes_force_delete"),
            archiveList: has("productTypes_archive_list"),
            restore: has("productTypes_restore"),
            restoreAll: has("productTypes_restore-all-table"),
            deleteAll: has("productTypes_delete-all-table"),
            massForceDeleteAll: has("productTypes_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/product-types/archive");

    // CRUD Hooks
    const { deleteProductTypes, isDelete } = useDeleteProductTypes();
    const { deleteAllProductTypes, isDeleteAll } = useDeleteAllProductTypes();
    const { restoreProductType, isRestore } = useRestoreProductType();
    const { restoreAllProductTypes, isRestoreAll } = useRestoreAllProductTypes();
    const { forceDeleteProductType, isForceDelete } = useForceDeleteProductType();
    const { forceDeleteAllProductTypes, isForceDeleteAll } = useForceDeleteAllProductTypes();

    // ✅ استخدام Custom Hook للإجراءات المشتركة
    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/product-types",
        deleteSingle: deleteProductTypes,
        deleteAll: deleteAllProductTypes,
        restoreSingle: restoreProductType,
        restoreAll: restoreAllProductTypes,
        forceDeleteSingle: forceDeleteProductType,
        forceDeleteAll: forceDeleteAllProductTypes,
        isArchive,
    });

    // ✨ Local State (specific to this table)
    const [statusFilter, setStatusFilter] = React.useState("");

    // ✨ Filters including status and sorting
    const filters = useMemo(
        () => ({
            search: search || undefined,
            status: statusFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, statusFilter, sortBy, sortDir, isArchive]
    );

    // ✨ Filter configuration for TableFilterBar
    const filterConfig = useMemo(
        () => [
            {
                type: "select",
                name: "status",
                label: { ar: "الحالة", en: "Status" },
                options: [
                    { value: "", label: { ar: "الكل", en: "All" } },
                    { value: "active", label: { ar: "نشط", en: "Active" } },
                    { value: "disabled", label: { ar: "معطل", en: "Disabled" } },
                ],
                value: statusFilter,
                onChange: setStatusFilter,
            },
        ],
        [statusFilter]
    );

    // ✨ Clear all filters handler
    const handleClearAllFilters = () => {
        setStatusFilter("");
    };

    const { isLoading, error, productTypes, meta } = useGetProductTypes(
        page,
        perPage,
        filters
    );

    const items = useMemo(() => productTypes?.items || [], [productTypes]);

    const rowMenuDisabled = isArchive
        ? !perms.restore && !perms.forceDelete
        : !perms.edit && !perms.delete;

    const renderRow = (params) => (
        <ProductTypesTableRow
            key={params.item.id}
            productType={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive
        ? !perms.massForceDeleteAll
        : !perms.deleteAll;

    const title = isArabic ? "أنواع المنتجات" : "Product Types";
    const subtitle = isArchive
        ? isArabic
            ? "عرض أنواع المنتجات المحذوفة (الأرشيف)"
            : "Archived product types (soft deleted)"
        : isArabic
            ? "إدارة أنواع المنتجات المتاحة في النظام"
            : "Manage available product types in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة نوع منتج"
            : "Create new product type";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic
                        ? "ليس لديك صلاحية للاستعادة"
                        : "No restore permission"
                );
                return;
            }

            if (isRestoreAll) return;

            if (!selectedIds.length) {
                toast.error(
                    isArabic
                        ? "الرجاء تحديد العناصر المراد استرجاعها أولاً"
                        : "Please select items to restore first"
                );
                return;
            }

            restoreAllProductTypes({ items: selectedIds });
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic
                        ? "ليس لديك صلاحية للإنشاء"
                        : "No create permission"
                );
                return;
            }

            navigate("/product-types/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/product-types/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/product-types"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={ProductTypesTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية تنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={
                    isArchive ? "لا توجد أنواع منتجات في الأرشيف" : "لا توجد أنواع منتجات"
                }
                emptyLabelEn={isArchive ? "No archived product types" : "No product types"}
                loadErrorAr="حدث خطأ أثناء تحميل أنواع المنتجات"
                loadErrorEn="Failed to load product types"
                filters={filterConfig}
                onClearAllFilters={handleClearAllFilters}
            />
        </>
    );
};

export default ProductTypesTable;
